import { useState, useEffect, useMemo, memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import MistakesTable from '../Components/MistakesTable';
import AttemptPatternPieChartCard from '../Components/AttemptPatternPieChartCard';
import MistakesLineChart from '../Components/MistakesLineChart';
import { CardSkeleton, MistakesTableSkeleton } from '../Components/MistakesLoaders';

const MemoizedHeader = memo(() => (
  <div className="mb-6">
    <h2 className="text-2xl font-semibold text-[var(--q3-neutral-default)] mb-2">Test-wise Mistakes</h2>
    <p className="text-[var(--q3-neutral-light)] text-xs">
      Revisit questions which you either attempted incorrectly or didn't attempt in the paper
    </p>
  </div>
));

export default function TestWiseMistakes() {
  const navigate = useNavigate();
  const [mistakesOverviewExpanded, setMistakesOverviewExpanded] = useState(true);
  const [attemptingPatternExpanded, setAttemptingPatternExpanded] = useState(false);
  const [activeButtonFilter, setActiveButtonFilter] = useState(null);
  const [sortBy, setSortBy] = useState('default');
  const [overviewFilters, setOverviewFilters] = useState([]);
  const [testTypeOptions, setTestTypeOptions] = useState([]);
  const [testsOverviewData, setTestsOverviewData] = useState([]);
  const [attemptOverviewPieData, setAttemptOverviewPieData] = useState([]);
  const [selectedTestType, setSelectedTestType] = useState('All Tests');
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [areFiltersLoading, setAreFiltersLoading] = useState(true);
  const [isTestTypeSelectOpen, setIsTestTypeSelectOpen] = useState(false);
  const location = useLocation();

  const selectedPackId = localStorage.getItem('selectedPackId');

  // Combined useEffect for filter fetching and initial state setting from URL
  useEffect(() => {
    setAreFiltersLoading(true);
    axios
      .get(`/api/v2/answers/filters`, {
        params: { page: 'test-wise' },
      })
      .then((response) => {
        if (response.data.success) {
          const booleanFilters = response.data.data.filters.filter((f) => f.boolean);
          setOverviewFilters(booleanFilters);

          const queryParams = new URLSearchParams(location.search);
          const testCount = queryParams.get('test-count');

          if (testCount && (testCount === '3' || testCount === '5')) {
            setActiveButtonFilter(`last${testCount}Tests`);
          } else {
            if (booleanFilters.length > 0) {
              setActiveButtonFilter(booleanFilters[0].key);
            } else {
              setActiveButtonFilter('all');
            }
          }
        }
      })
      .catch((error) => console.error('Error fetching test-wise filters:', error))
      .finally(() => setAreFiltersLoading(false));
  }, [location.search]);

  useEffect(() => {
    if (selectedPackId && activeButtonFilter) {
      setIsDataLoading(true);

      const params = {
        offset: 0,
        limit: 10,
        ...(activeButtonFilter !== 'all' && { [activeButtonFilter]: 'true' }),
        ...(selectedTestType && selectedTestType !== 'All Tests' && { testType: selectedTestType }),
      };

      axios
        .get(`/api/v2/answers/${selectedPackId}/test-wise`, { params })
        .then((response) => {
          if (response.data.success) {
            const { testTypeFilterValues = [], tests = [], attemptOverview = [] } = response.data.data;
            setTestTypeOptions(['All Tests', ...testTypeFilterValues]);
            setTestsOverviewData(tests);
            setAttemptOverviewPieData(attemptOverview);

            if (!selectedTestType && testTypeFilterValues.length > 0) {
              setSelectedTestType('All Tests');
            }
          }
        })
        .catch((error) => console.error('Error fetching test-wise mistakes:', error))
        .finally(() => setIsDataLoading(false));
    }
  }, [selectedPackId, activeButtonFilter, selectedTestType]);

  const handleTestClick = (test) => {
    navigate(`/v3/my-mistakes/test/${test.testId}`, {
      state: { testTitle: test.testTitle },
    });
  };

  const lineChartData = useMemo(
    () =>
      testsOverviewData.map((test) => ({
        name: test.testTitle,
        incorrect: test.totalIncorrect,
        correct: test.totalCorrect,
        notAttempted: test.totalUnattempted,
      })),
    [testsOverviewData],
  );

  const lineChartLines = [
    { dataKey: 'incorrect', label: 'Incorrect Qs', color: 'var(--q3-danger-normal)' },
    { dataKey: 'correct', label: 'Correct Qs', color: 'var(--q3-success-normal)' },
    { dataKey: 'notAttempted', label: 'Not attempted Qs', color: 'var(--q3-neutral-light)' },
  ];

  useEffect(() => {
    if (lineChartData.length > 0) {
    }
  }, [lineChartData]);

  return (
    <div>
      <MemoizedHeader />

      {/* <div className="flex items-center flex-wrap gap-2 mb-6">
        <Select value={selectedTestType} onValueChange={setSelectedTestType} onOpenChange={setIsTestTypeSelectOpen}>
          <SelectTrigger className={`cursor-pointer w-auto rounded-full`}>
            Test Type : <SelectValue placeholder="Test Type" />
          </SelectTrigger>
          <SelectContent>
            {testTypeOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="h-8 w-[2px] bg-[var(--q3-stroke-normal)]" />
        {!areFiltersLoading &&
          overviewFilters.map(
            (filter) =>
              filter.boolean && (
                <Button
                  key={filter.key}
                  variant={activeButtonFilter === filter.key ? 'default' : 'outline'}
                  size="sm"
                  className={
                    activeButtonFilter === filter.key
                      ? 'bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)] rounded-full'
                      : 'rounded-full'
                  }
                  onClick={() => setActiveButtonFilter(filter.key)}
                >
                  {filter.title}
                  {activeButtonFilter === filter.key && <Check className="ml-2 h-4 w-4" />}
                </Button>
              ),
          )}
      </div> */}

      {isDataLoading || areFiltersLoading ? (
        <>
          {activeButtonFilter === 'all' && <CardSkeleton />}
          <CardSkeleton />
          <MistakesTableSkeleton />
        </>
      ) : (
        <>
          {/* {activeButtonFilter === 'all' && (
            <MistakesLineChart
              title="Your Mistakes Overview"
              description="This graph shows how you performed through different tests you attempted"
              data={lineChartData}
              expanded={mistakesOverviewExpanded}
              onToggleExpand={() => setMistakesOverviewExpanded(!mistakesOverviewExpanded)}
              lines={lineChartLines}
              xAxisDataKey="name"
            />
          )} */}

          <AttemptPatternPieChartCard
            data={attemptOverviewPieData}
            expanded={attemptingPatternExpanded}
            onToggleExpand={() => setAttemptingPatternExpanded(!attemptingPatternExpanded)}
          />

          <MistakesTable
            title="Test-wise Mistakes"
            sortBy={sortBy}
            showDate={true}
            onSortByChange={setSortBy}
            data={testsOverviewData}
            onRowClick={handleTestClick}
            nameColumnTitle="Test name"
          />
        </>
      )}
    </div>
  );
}
